import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Popover, IconButton, Button, TextField, InputAdornment } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { CalendarMonth as CalendarMonthIcon } from "@mui/icons-material";
import { MonthNavigationHeader, formatCalendarWeekday, sharedCalendarSlotProps, parseToDayjs } from "../HelperFunctions/EmployeeFormHelpers";

dayjs.extend(quarterOfYear);

export const ViewTransactionsRanges = () => {
    const dateOptions = [
        {
            date: "Today",
            actualValue: {
                start: dayjs().startOf("day"),
                end: dayjs().endOf("day"),
            },
        },
        {
            date: "Yesterday",
            actualValue: {
                start: dayjs().subtract(1, "day").startOf("day"),
                end: dayjs().subtract(1, "day").endOf("day"),
            },
        },
        {
            date: "Recent",
            actualValue: {
                start: dayjs().subtract(14, "day"),
                end: dayjs(),
            },
        },
        {
            date: "This Week",
            actualValue: {
                start: dayjs().startOf("week"),
                end: dayjs().endOf("week"),
            },
        },
        {
            date: "This Week – to – Date",
            actualValue: {
                start: dayjs().startOf("week"),
                end: dayjs(),
            },
        },
        {
            date: "Last Week",
            actualValue: {
                start: dayjs().subtract(1, "week").startOf("week"),
                end: dayjs().subtract(1, "week").endOf("week"),
            },
        },
        {
            date: "Last Week–to–Date",
            actualValue: {
                start: dayjs().subtract(1, "week").startOf("week"),
                end: dayjs(),
            },
        },
        {
            date: "Next Week",
            actualValue: {
                start: dayjs().add(1, "week").startOf("week"),
                end: dayjs().add(1, "week").endOf("week"),
            },
        },
        {
            date: "Next Four Weeks",
            actualValue: {
                start: dayjs().add(1, "week").startOf("week"),
                end: dayjs().add(4, "week").endOf("week"),
            },
        },
        {
            date: "This Month",
            actualValue: {
                start: dayjs().startOf("month"),
                end: dayjs().endOf("month"),
            },
        },
        {
            date: "This Month – to – Date",
            actualValue: {
                start: dayjs().startOf("month"),
                end: dayjs(),
            },
        },
        {
            date: "Last Month",
            actualValue: {
                start: dayjs().subtract(1, "month").startOf("month"),
                end: dayjs().subtract(1, "month").endOf("month"),
            },
        },
        {
            date: "Last Month – to – Date",
            actualValue: {
                start: dayjs().subtract(1, "month").startOf("month"),
                end: dayjs(),
            },
        },
        {
            date: "Next Month",
            actualValue: {
                start: dayjs().add(1, "month").startOf("month"),
                end: dayjs().add(1, "month").endOf("month"),
            },
        },
        {
            date: "This Quarter",
            actualValue: {
                start: dayjs().startOf("quarter"),
                end: dayjs().endOf("quarter"),
            },
        },
        {
            date: "This Quarter – to – Date",
            actualValue: {
                start: dayjs().startOf("quarter"),
                end: dayjs(),
            },
        },
        {
            date: "Last Quarter",
            actualValue: {
                start: dayjs().subtract(1, "quarter").startOf("quarter"),
                end: dayjs().subtract(1, "quarter").endOf("quarter"),
            },
        },
        {
            date: "Last Quarter – to – Date",
            actualValue: {
                start: dayjs().subtract(1, "quarter").startOf("quarter"),
                end: dayjs(),
            },
        },
        {
            date: "Next Quarter",
            actualValue: {
                start: dayjs().add(1, "quarter").startOf("quarter"),
                end: dayjs().add(1, "quarter").endOf("quarter"),
            },
        },
        {
            date: "This Year",
            actualValue: {
                start: dayjs().startOf("year"),
                end: dayjs().endOf("year"),
            },
        },
        {
            date: "This Year – to – Date",
            actualValue: {
                start: dayjs().startOf("year"),
                end: dayjs(),
            },
        },
        {
            date: "Last Year",
            actualValue: {
                start: dayjs().subtract(1, "year").startOf("year"),
                end: dayjs().subtract(1, "year").endOf("year"),
            },
        },
        {
            date: "Last Year – to – Date",
            actualValue: {
                start: dayjs().subtract(1, "year").startOf("year"),
                end: dayjs(),
            },
        },
        {
            date: "Next Year",
            actualValue: {
                start: dayjs().add(1, "year").startOf("year"),
                end: dayjs().add(1, "year").endOf("year"),
            },
        },
        {
            date: "Range",
            actualValue: {
                start: null,
                end: null,
            },
        },
        {
            date: "Past 30 Days",
            actualValue: {
                start: dayjs().subtract(30, "day"),
                end: dayjs(),
            },
        },
    ];
    return dateOptions;
};

export const sharedProps = {
    variant: "standard",
    placeholder: "",
    InputProps: {
        disableUnderline: true,
        sx: {
            fontSize: "11px",
            fontWeight: 500,
            height: "18px",
            minHeight: "18px",
            "& .MuiSvgIcon-root": {
                fontSize: "18px",
                marginRight: "2px",
            },
        },
    },
    sx: {
        minWidth: 110,
        maxWidth: 130,
        background: "transparent",
        height: "18px",
    },
};

const formatToDisplay = (val) => {
    if (!val) return "";
    const d = parseToDayjs(val);
    return d && d.isValid() ? d.format("MM/DD/YYYY") : (typeof val === "string" ? val : "");
};

const isSameDate = (d1, d2) => {
    if (!d1 && !d2) return true;
    if (!d1 || !d2) return false;
    const day1 = dayjs(d1);
    const day2 = dayjs(d2);
    if (!day1.isValid() && !day2.isValid()) return true;
    if (!day1.isValid() || !day2.isValid()) return false;
    return day1.isSame(day2, "day");
};

const DateRangeField = ({ value, onChange, placeholder, minDate, maxDate }) => {
    const [inputValue, setInputValue] = useState(() => formatToDisplay(value));
    const [anchorEl, setAnchorEl] = useState(null);
    const isCalendarOpen = Boolean(anchorEl);

    const parsedCalendarDate = useMemo(() => {
        if (!value && !inputValue) return null;
        const d = parseToDayjs(value || inputValue);
        return d && d.isValid() ? d : null;
    }, [value, inputValue]);

    const [calendarDate, setCalendarDate] = useState(() => parsedCalendarDate || null);
    const [currentView, setCurrentView] = useState("day");
    const currentViewRef = React.useRef("day");

    const updateView = (newView) => {
        currentViewRef.current = newView;
        setCurrentView(newView);
    };

    useEffect(() => {
        currentViewRef.current = currentView;
    }, [currentView]);

    useEffect(() => {
        const formatted = formatToDisplay(value);
        setInputValue(formatted);
        const d = parseToDayjs(value);
        setCalendarDate(d && d.isValid() ? d : null);
    }, [value]);

    const handleInputChange = (e) => {
        const raw = e.target.value;

        if (!raw.trim()) {
            setInputValue("");
            if (value !== null && value !== undefined && value !== "") {
                onChange(null);
            }
            return;
        }

        const cleanDigits = raw.replace(/\D/g, "");
        if (cleanDigits.length === 8) {
            const parsed = parseToDayjs(raw);
            if (parsed && parsed.isValid()) {
                setInputValue(parsed.format("MM/DD/YYYY"));
                if (!isSameDate(value, parsed)) {
                    onChange(parsed);
                }
                return;
            }
        }

        setInputValue(raw);
    };

    const handleBlur = () => {
        if (!inputValue.trim()) {
            setInputValue("");
            if (value !== null && value !== undefined && value !== "") {
                onChange(null);
            }
            return;
        }

        const parsed = parseToDayjs(inputValue);

        if (parsed && parsed.isValid()) {
            setInputValue(parsed.format("MM/DD/YYYY"));
            if (!isSameDate(value, parsed)) {
                onChange(parsed);
            }
        } else {
            setInputValue(formatToDisplay(value));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleBlur();
        }
    };

    const handleOpenCalendar = (event) => {
        setAnchorEl(event.currentTarget);
        updateView("day");
        const initialDate = parsedCalendarDate && parsedCalendarDate.isValid() ? parsedCalendarDate : null;
        setCalendarDate(initialDate);
    };

    const handleCloseCalendar = () => {
        setAnchorEl(null);
        updateView("day");
    };

    const handleDateChange = (newDate) => {
        if (!newDate || !dayjs(newDate).isValid()) return;

        const activeView = currentViewRef.current || currentView;

        if (activeView === "year") {
            const base = (calendarDate && calendarDate.isValid()) ? calendarDate : (parsedCalendarDate && parsedCalendarDate.isValid() ? parsedCalendarDate : dayjs());
            const targetYear = dayjs(newDate).year();
            const maxDays = dayjs().year(targetYear).month(base.month()).daysInMonth();
            const safeDay = Math.min(base.date(), maxDays);
            const updated = base.year(targetYear).date(safeDay);
            setCalendarDate(updated);
            updateView("month");
            return;
        }

        if (activeView === "month") {
            const base = (calendarDate && calendarDate.isValid()) ? calendarDate : (parsedCalendarDate && parsedCalendarDate.isValid() ? parsedCalendarDate : dayjs());
            const targetMonth = dayjs(newDate).month();
            const targetYear = dayjs(newDate).year() || base.year();
            const maxDays = dayjs().year(targetYear).month(targetMonth).daysInMonth();
            const safeDay = Math.min(base.date(), maxDays);
            const updated = base.year(targetYear).month(targetMonth).date(safeDay);
            setCalendarDate(updated);
            updateView("day");
            return;
        }

        // Final day selection in day view
        setCalendarDate(newDate);
        const d = dayjs(newDate);
        setInputValue(d.format("MM/DD/YYYY"));
        if (!isSameDate(value, d)) {
            onChange(d);
        }
        handleCloseCalendar();
    };

    const handleClearDate = () => {
        setInputValue("");
        setCalendarDate(null);
        if (value !== null && value !== undefined && value !== "") {
            onChange(null);
        }
        handleCloseCalendar();
    };

    const handleTodayDate = () => {
        const today = dayjs();
        setCalendarDate(today);
        setInputValue(today.format("MM/DD/YYYY"));
        if (!isSameDate(value, today)) {
            onChange(today);
        }
        handleCloseCalendar();
    };

    return (
        <>
            <TextField
                variant="standard"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                        <InputAdornment position="end" sx={{ margin: 0 }}>
                            <IconButton
                                size="small"
                                onClick={handleOpenCalendar}
                                tabIndex={-1}
                                sx={{
                                    padding: "1px",
                                    color: "#555",
                                    "&:hover": { color: "#000" },
                                    "& .MuiSvgIcon-root": {
                                        fontSize: "18px",
                                    },
                                }}
                            >
                                <CalendarMonthIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: {
                        fontSize: "11px",
                        fontWeight: 500,
                        height: "18px",
                        minHeight: "18px",
                        padding: 0,
                    },
                }}
                inputProps={{
                    style: {
                        padding: "0 2px",
                        fontSize: "11px",
                        fontWeight: 500,
                        height: "18px",
                        color: "#000",
                    },
                }}
                sx={{
                    minWidth: 110,
                    maxWidth: 130,
                    background: "transparent",
                    height: "18px",
                }}
            />

            <Popover
                open={isCalendarOpen}
                anchorEl={anchorEl}
                onClose={handleCloseCalendar}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                PaperProps={{
                    sx: {
                        minWidth: "260px",
                        width: "260px",
                        boxShadow: "0px 4px 16px rgba(0,0,0,0.18)",
                        borderRadius: "4px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        ...sharedCalendarSlotProps.popper?.sx,
                    },
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "#3d5c7b",
                        color: "white",
                        minHeight: "26px",
                        height: "26px",
                        padding: "0 8px",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ...sharedCalendarSlotProps.toolbar?.sx,
                    }}
                >
                    <Typography
                        onClick={() => updateView("year")}
                        sx={{
                            position: "absolute",
                            left: 8,
                            color: "white",
                            fontSize: "11px",
                            fontWeight: 400,
                            lineHeight: "26px",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.85 }
                        }}
                    >
                        {(calendarDate && calendarDate.isValid() ? calendarDate : (parsedCalendarDate || dayjs())).format("YYYY")}
                    </Typography>
                    <Typography
                        onClick={() => updateView(currentView === "day" ? "month" : "day")}
                        sx={{
                            color: "white",
                            fontSize: "11px",
                            fontWeight: 400,
                            lineHeight: "26px",
                            textAlign: "center",
                            cursor: "pointer",
                            "&:hover": { opacity: 0.85 }
                        }}
                    >
                        {(calendarDate && calendarDate.isValid() ? calendarDate : (parsedCalendarDate || dayjs())).format("ddd, DD MMM")}
                    </Typography>
                </Box>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                        value={calendarDate && calendarDate.isValid() ? calendarDate : (parsedCalendarDate || null)}
                        onChange={handleDateChange}
                        onMonthChange={(newMonth) => {
                            setCalendarDate((prev) => {
                                const base = prev && prev.isValid() ? prev : dayjs();
                                return base.year(newMonth.year()).month(newMonth.month());
                            });
                        }}
                        minDate={minDate}
                        maxDate={maxDate}
                        dayOfWeekFormatter={formatCalendarWeekday}
                        slots={{
                            calendarHeader: MonthNavigationHeader,
                        }}
                        view={currentView}
                        onViewChange={(newView) => updateView(newView)}
                        views={["year", "month", "day"]}
                        referenceDate={calendarDate && calendarDate.isValid() ? calendarDate : (parsedCalendarDate || dayjs())}
                        sx={{
                            width: "260px",
                            height: "230px",
                            margin: 0,
                            ...sharedCalendarSlotProps.popper?.sx?.["& .MuiDateCalendar-root"],
                        }}
                    />
                </LocalizationProvider>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "6px 10px",
                        borderTop: "1px solid #e0e0e0",
                        backgroundColor: "white",
                        ...sharedCalendarSlotProps.actionBar?.sx,
                    }}
                >
                    <Button
                        size="small"
                        onClick={handleClearDate}
                        sx={{ fontSize: "11px", textTransform: "none", color: "#555", minWidth: "50px" }}
                    >
                        Clear
                    </Button>
                    <Button
                        size="small"
                        onClick={handleTodayDate}
                        sx={{ fontSize: "11px", textTransform: "none", color: "#2f5d8a", fontWeight: 400, minWidth: "50px" }}
                    >
                        Today
                    </Button>
                    <Button
                        size="small"
                        onClick={handleCloseCalendar}
                        sx={{ fontSize: "11px", textTransform: "none", color: "#555", minWidth: "50px" }}
                    >
                        OK
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

const CustomeDateRange = ({
    startDate,
    endDate,
    onStartChange,
    onEndChange,
    width = "fit-content",
    height = "35px",
    sx = {},
}) => {
    return (
        <Box
            border={1}
            borderColor="#d3d3d3"
            borderRadius={1}
            bgcolor="#F5F5F5"
            px={1}
            width={width}
            height={height}
            display="flex"
            alignItems="center"
            sx={{ boxSizing: "border-box", ...sx }}
        >
            <Box 
                display="flex"
                gap={1}
                sx={{
                    width: "100%",
                    justifyContent: width === "100%" ? "space-between" : "flex-start",
                    alignItems: "center",
                }}
            >
                <Box display="flex" flexDirection="column" justifyContent="center">
                    <Typography sx={{ fontSize: "8px", color: "#333", lineHeight: 1 }}>From</Typography>
                    <DateRangeField
                        value={startDate}
                        placeholder="Start"
                        maxDate={endDate ? dayjs(endDate) : undefined}
                        onChange={(newValue) => {
                            if (!isSameDate(startDate, newValue)) {
                                onStartChange?.(newValue);
                                if (endDate && newValue && dayjs(endDate).isBefore(newValue)) {
                                    onEndChange?.(null);
                                }
                            }
                        }}
                    />
                </Box>

                <Box display="flex" flexDirection="column" justifyContent="center">
                    <Typography sx={{ fontSize: "8px", color: "#333", lineHeight: 1 }}>To</Typography>
                    <DateRangeField
                        value={endDate}
                        placeholder="End"
                        minDate={startDate ? dayjs(startDate) : undefined}
                        onChange={(newValue) => {
                            if (!isSameDate(endDate, newValue)) {
                                onEndChange?.(newValue);
                                if (startDate && newValue && dayjs(newValue).isBefore(startDate)) {
                                    onStartChange?.(null);
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default CustomeDateRange;
